import type { SubscriptionFrequencyInfo } from 'autogen/swagger/all';
import { useUpdateFrequencyMutation } from 'autogen/swagger/all/hooks';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import { updateBasket } from 'components/basket/shared/basket';
import Select, { type IOption } from 'components/shared/inputs/select';
import track from 'modules/tracking/dataLayer';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'components/Toast';

export default function SubscriptionFrequencySelect({
    model: { choosable, selected, productId },
    ...restProps
}: {
    model: SubscriptionFrequencyInfo;
}) {
    const { t } = useTranslation();
    const { mutateAsync: updateFrequency } = useUpdateFrequencyMutation();
    const ga4Tracking = useGA4Tracking();

    const options: IOption[] | null = useMemo(() => {
        if (!selected || !choosable.length) return null;

        const frequencyGroup = choosable.find((group) => group.id === selected.groupId);

        if (!frequencyGroup) return null;

        return frequencyGroup.frequencies.map((frequency) => ({
            label: frequency.nameWithRecommendationText,
            value: frequency.id.toString()
        }));
    }, [selected, choosable]);

    if (!options) return null;

    const selectedValue = options.find((frequency) => frequency.value === selected?.id.toString());

    const handleOnChange = async ({ value, label }) => {
        try {
            const data = await updateFrequency({ params: { subscriptionFrequencyId: value, productId } });
            updateBasket();

            if (!data.success) {
                throw Error(`Response not successful (${data.message})`);
            }
            if (data.dataLayerDto) {
                track(data.dataLayerDto);
            }

            UAEventTracking({
                eventCategory: 'basket',
                eventAction: 'changeFrequency',
                eventLabel: label
            });
            ga4Tracking({
                eventName: 'change_subscription_frequency',
                category: 'ecommerce',
                eventParams: [
                    { param_name: 'frequency', param_value: label },
                    { param_name: 'checkout_step', param_value: 1 }
                ]
            });
        } catch (error) {
            toast.error<string>(t(GeneralExceptionError), {
                position: 'top-right'
            });
            console.error(error);
        }
    };

    return <Select {...restProps} options={options} value={selectedValue} onChange={handleOnChange} />;
}
